import { Component } from '@theme/component';

let sequence = 0;
const element = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

// Cards are moved, never recreated: native product forms, variants and delivery
// fields retain their identities. Draft previews have no order form at all.
class DrinkFamily extends Component {
  #controller;
  #cards = [];
  #select;
  #summary;
  #media;
  #name;
  #money;
  #volume;
  #bodyVolume;
  #status;
  #chosen;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const signal = this.#controller.signal;
    this.#cards = Array.from(this.querySelectorAll('[data-drink-family]')).filter(card => card.closest('upupa-drink-family') === this);
    if (!this.#cards.length) return;
    if (!this.querySelector(':scope > details')) this.#build();
    this.#select = this.querySelector('[data-family-select]');
    this.#summary = this.querySelector('[data-family-summary]');
    this.#media = this.querySelector('[data-family-media]');
    this.#name = this.querySelector('[data-family-name]');
    this.#money = this.querySelector('[data-family-money]');
    this.#volume = this.querySelector('[data-family-volume]');
    this.#bodyVolume = this.querySelector('[data-family-body-volume]');
    this.#status = this.querySelector('[data-family-status]');
    this.#select.addEventListener('change', () => this.#choose(this.#select.value, true), { signal });
    this.addEventListener('change', event => {
      if (event.target !== this.#select) this.#updateSummary();
    }, { signal });
    this.addEventListener('submit', event => {
      const card = event.target.closest('[data-drink-family]');
      if (card && (card !== this.#chosen || card.hasAttribute('data-drink-preview') || card.hidden)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { signal, capture: true });
    const quantityCard = this.#cards.find(card => Number(card.querySelector('[data-party-qty]')?.value) > 0);
    this.#choose(quantityCard?.dataset.handle || this.#select.value, false);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  #build() {
    const first = this.#cards[0];
    const panel = element('details', 'upupa-family__panel');
    const summary = element('summary', 'upupa-family__summary');
    summary.dataset.familySummary = '';
    const media = element('span', 'upupa-family__media');
    media.dataset.familyMedia = '';
    const copy = element('span', 'upupa-family__copy');
    copy.append(element('span', 'upupa-family__label', first.dataset.drinkLabel));
    const name = element('span', 'upupa-family__name');
    name.dataset.familyName = '';
    const money = element('span', 'upupa-family__money');
    money.dataset.familyMoney = '';
    const volume = element('span', 'upupa-family__volume');
    volume.dataset.familyVolume = '';
    const status = element('span', 'upupa-family__status');
    status.dataset.familyStatus = '';
    copy.append(name, money, volume, status, element('span', 'upupa-family__hint', first.dataset.drinkChoose + ' +'));
    summary.append(media, copy);
    const body = element('div', 'upupa-family__body');
    const label = element('label', 'upupa-family__select-label', first.dataset.drinkChoose);
    const select = element('select', 'upupa-family__select');
    select.dataset.familySelect = '';
    select.id = `UpupaDrinkType-${++sequence}`;
    label.htmlFor = select.id;
    const branches = new Map();
    this.#cards.forEach(card => {
      let parent = select;
      const branch = card.dataset.drinkBranch;
      if (branch) {
        if (!branches.has(branch)) {
          const group = element('optgroup');
          group.label = branch;
          select.append(group);
          branches.set(branch, group);
        }
        parent = branches.get(branch);
      }
      const option = element('option', '', card.dataset.drinkTitle);
      option.value = card.dataset.handle;
      parent.append(option);
    });
    const choices = element(this.#cards[0].tagName === 'LI' ? 'ul' : 'div', 'upupa-family__choices');
    this.#cards.forEach(card => choices.append(card));
    const bodyVolume = element('span', 'upupa-family__volume');
    bodyVolume.dataset.familyBodyVolume = '';
    body.append(label, select, bodyVolume, choices);
    const feedback = element('span', 'visually-hidden');
    feedback.dataset.familyFeedback = '';
    feedback.setAttribute('role', 'status');
    feedback.setAttribute('aria-live', 'polite');
    feedback.setAttribute('aria-atomic', 'true');
    panel.append(summary, body, feedback);
    this.append(panel);
  }

  #choose(handle, transferQuantity) {
    const card = this.#cards.find(item => item.dataset.handle === handle);
    if (!card) return;
    const oldQuantity = this.#chosen?.querySelector('[data-party-qty]');
    const newQuantity = card.querySelector('[data-party-qty]');
    // Switching a bulk-order choice cannot leave an old hidden quantity behind.
    if (transferQuantity && this.#chosen !== card && oldQuantity) {
      const count = oldQuantity.value;
      if (Number(count) > 0 && (!newQuantity || newQuantity.disabled)) {
        this.#select.value = this.#chosen.dataset.handle;
        return;
      }
      oldQuantity.value = '0';
      if (newQuantity && !newQuantity.disabled) newQuantity.value = count;
    }
    this.#chosen = card;
    this.#select.value = handle;
    this.#cards.forEach(item => { item.hidden = item !== card; });
    this.dataset.selectedHandle = handle;
    this.dataset.selectedProduct = card.querySelector('product-form-component')?.dataset.productId || card.dataset.productId || card.dataset.drinkProductId || '';
    this.dataset.selectedVolume = card.dataset.drinkVolume || '';
    this.#updateSummary();
    if (transferQuantity) {
      this.querySelector('[data-family-feedback]').textContent = this.#summary.textContent;
      newQuantity?.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  #updateSummary() {
    if (!this.#chosen) return;
    this.#name.textContent = this.#chosen.dataset.drinkTitle;
    this.#money.textContent = this.#chosen.querySelector('.upupa-choice__money, .upupa-party__price, .p')?.textContent.trim() || this.#chosen.dataset.drinkPrice;
    this.#volume.textContent = this.#chosen.dataset.drinkVolume || '';
    this.#bodyVolume.textContent = this.#chosen.hasAttribute('data-drink-preview') ? '' : this.#chosen.dataset.drinkVolume || '';
    this.#status.textContent = this.#chosen.querySelector('.upupa-family-preview__status, .upupa-add__sold')?.textContent.trim() || '';
    this.#media.replaceChildren();
    const image = this.#chosen.querySelector('img');
    if (image) {
      const copy = image.cloneNode(false);
      copy.removeAttribute('id');
      copy.removeAttribute('ref');
      copy.alt = '';
      this.#media.append(copy);
    } else {
      this.#media.append(element('span', 'upupa-family__empty', this.#chosen.querySelector('.upupa-noimg')?.textContent || ''));
    }
  }

  filter(term) {
    const matches = this.#cards.filter(card => card.dataset.drinkTitle.toLocaleLowerCase('fi-FI').includes(term));
    this.hidden = !matches.length;
    const positive = Number(this.#chosen?.querySelector('[data-party-qty]')?.value) > 0;
    if (matches.length && !matches.includes(this.#chosen) && !positive) this.#choose(matches[0].dataset.handle, false);
    return matches.length;
  }
}

class DrinkFamilies extends Component {
  #observer;
  #queued = false;

  connectedCallback() {
    super.connectedCallback();
    const root = this.parentElement;
    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => this.#schedule(root));
    this.#observer.observe(root, { childList: true, subtree: true });
    this.#schedule(root);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }

  #schedule(root) {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      if (!this.isConnected) return;
      root.querySelectorAll('.upupa-grid, .upupa-dishes__grid, .upupa-sp__menu, .upupa-party__grid, .upupa-cycle__daily-group > ul').forEach(container => {
        // Calendar/same-day source pools must remain unenhanced for native clones.
        if (container.closest('[data-el="pool"], .upupa--home-order [data-el="extras"]')) return;
        const families = new Map();
        Array.from(container.children).filter(card => card.hasAttribute('data-drink-family')).forEach(card => {
          const key = card.dataset.drinkFamily;
          if (!families.has(key)) families.set(key, []);
          families.get(key).push(card);
        });
        families.forEach(cards => {
          if (cards.length < 2) return;
          if (cards.filter(card => Number(card.querySelector('[data-party-qty]')?.value) > 0).length > 1) return;
          const host = element('upupa-drink-family', 'upupa-family');
          host.dataset.family = cards[0].dataset.drinkFamily;
          const entry = container.tagName === 'UL' ? element('li', 'upupa-family-entry') : host;
          if (entry !== host) entry.append(host);
          const placeholder = document.createComment('UPUPA drink family');
          container.insertBefore(placeholder, cards[0]);
          cards.forEach(card => host.append(card));
          placeholder.replaceWith(entry);
        });
      });
    });
  }
}

if (!customElements.get('upupa-drink-family')) customElements.define('upupa-drink-family', DrinkFamily);
if (!customElements.get('upupa-drink-families')) customElements.define('upupa-drink-families', DrinkFamilies);
